/* ---------------------------------------------------------------------------
   Fuzzy match for Search (card UW7Lu06zMJp9, epic BpnqEtUIYiSi).

   Jonathan 2026-10-10: "fuzzy search by item description/tag type". A
   substring LIKE would fail the two cases the card actually names — a word
   typed wrong, and a word that appears only inside a longer tag — so the
   matcher here is deliberately NOT `includes`.

   Dependency-free and framework-free on purpose: the worker, the web bundle
   and the native port all need the same ranking, and a Fuse.js-style library
   would be a third copy of an algorithm whose whole job is to be identical
   everywhere. Pure functions only, so a self-check can assert the cases.

   Scoring is per query TOKEN (every token must land somewhere, otherwise a
   two-word query matches everything), then averaged, then weighted by the
   field it landed in — a title hit outranks a description hit.
   --------------------------------------------------------------------------- */

/** One searchable field of a row. `weight` scales the field's contribution. */
export interface FuzzyField {
  key: string;
  text: string | null | undefined;
  weight?: number;
}

/** A row that scored, with the fields the query actually hit. */
export interface FuzzyMatch<T> {
  row: T;
  score: number;
  /** Field keys that matched at least one token, best first. */
  matched: string[];
}

/** Case-, accent- and punctuation-insensitive, whitespace-collapsed. */
export function normalise(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Split into searchable words. Single characters are dropped — they match
 *  everything and only add noise to a sentence-length description. */
function words(text: string): string[] {
  return normalise(text).split(' ').filter(w => w.length > 0);
}

/** Levenshtein distance, capped: returns `max + 1` as soon as it is clear the
 *  answer exceeds `max`, so a bad candidate costs a row scan and nothing more. */
export function editDistanceWithin(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = new Array<number>(b.length + 1);
  let curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1;
    const swap = prev;
    prev = curr;
    curr = swap;
  }
  return prev[b.length];
}

/** How well ONE query token lands on ONE target word: 1 = exact, 0 = no hit.
 *  Ordered by how sure we are, so a typo never outranks a real prefix. */
function tokenWordScore(token: string, word: string): number {
  if (word === token) return 1;
  if (word.startsWith(token)) return 0.9 - (word.length - token.length) * 0.01;
  if (token.length >= 3 && word.includes(token)) return 0.75;
  /* Typo tolerance: one edit for 4+ characters, two for 8+ — "burthday" still
     finds "birthday", but a two-letter slip does not match the whole list. */
  if (token.length >= 4) {
    const allowed = token.length >= 8 ? 2 : 1;
    if (editDistanceWithin(token, word, allowed) <= allowed) {
      return token.length >= 8 ? 0.55 : 0.6;
    }
  }
  return 0;
}

/** Best score for one token across a field's words. */
function tokenScore(token: string, fieldWords: string[]): number {
  let best = 0;
  for (const word of fieldWords) {
    const s = tokenWordScore(token, word);
    if (s > best) best = s;
    if (best === 1) break;
  }
  return best;
}

/**
 * Score one row against a query. Returns 0 when ANY token misses every field —
 * a multi-word query is a conjunction, not a bag of words.
 *
 * Empty/one-character queries score 0: the screen shows its prompt instead of
 * every row in the notebook.
 */
export function scoreRow(query: string, fields: FuzzyField[]): { score: number; matched: string[] } {
  const tokens = words(query);
  if (tokens.length === 0) return { score: 0, matched: [] };

  const prepared = fields
    .filter(f => typeof f.text === 'string' && f.text.trim().length > 0)
    .map(f => ({ key: f.key, weight: f.weight ?? 1, words: words(f.text as string) }));
  if (prepared.length === 0) return { score: 0, matched: [] };

  let total = 0;
  const hits = new Map<string, number>();

  for (const token of tokens) {
    let bestTokenScore = 0;
    let bestField: string | null = null;
    for (const field of prepared) {
      const s = tokenScore(token, field.words) * field.weight;
      if (s > bestTokenScore) {
        bestTokenScore = s;
        bestField = field.key;
      }
    }
    if (bestTokenScore <= 0 || !bestField) return { score: 0, matched: [] };
    total += bestTokenScore;
    hits.set(bestField, (hits.get(bestField) ?? 0) + bestTokenScore);
  }

  const matched = [...hits.entries()].sort((a, b) => b[1] - a[1]).map(([key]) => key);
  return { score: total / tokens.length, matched };
}

/** Rank rows for a query, dropping misses. Ties break on the row's own label
 *  so the list is stable between keystrokes instead of shuffling. */
export function rankRows<T>(
  query: string,
  rows: T[],
  fieldsOf: (row: T) => FuzzyField[],
  labelOf?: (row: T) => string,
): Array<FuzzyMatch<T>> {
  const out: Array<FuzzyMatch<T>> = [];
  for (const row of rows) {
    const { score, matched } = scoreRow(query, fieldsOf(row));
    if (score > 0) out.push({ row, score, matched });
  }
  return out.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const la = labelOf ? labelOf(a.row) : '';
    const lb = labelOf ? labelOf(b.row) : '';
    return la.localeCompare(lb);
  });
}

/** Below this many characters the screen shows its prompt, not a result list. */
export const MIN_QUERY_LENGTH = 2;
